import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { formatDuration, intervalToDuration } from "date-fns";
import Card from "components/Card/Card";
import Input from "components/Input/Input";
import ColorInput from "components/Input/ColorInput";
import Button from "components/Button/Regular";
import Select from "components/Input/Select";
import Page from "components/Page/Page";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useActiveOrg } from "context/ActiveOrgContext";
import { createOrgAnimal } from "services/patients.service";
import vgToast from "utils/toast";
import {
  PATIENT_ACTIVATION_STATE,
  PATIENT_BLOOD_GROUP,
  PATIENT_DECEASED_STATUS,
  PATIENT_DESEXED_STATUS,
  PATIENT_GENDER,
  PATIENT_SPECIES,
  patientActivationOptions,
  patientBloodGroupOptions,
  patientDeceasedOptions,
  patientDesexedOptions,
  patientGenderOptions,
  patientSpeciesOptions,
  toSelectOptions,
} from "constants/patientEnums";

type FormValues = {
  patientsName: string;
  species: string;
  active: string;
  dateOfBirth: string;
  alive: string;
  deceasedDate: string;
  mainColour: string;
  secondaryColour: string;
  microchipNumber: string;
  insuranceRefNumber: string;
  passportNumber: string;
  sex: string;
  bloodGroup: string;
  desexedStatus: string;
};

const normalizeHexColor = (value: string | undefined, fallback = "#000000") => {
  if (!value) return fallback;
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
};

const toApiDate = (value: string) => (value ? `${value}T00:00:00.000Z` : undefined);

const todayIso = () => new Date().toISOString().slice(0, 10);

const NewPatient = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      patientsName: "",
      species: String(PATIENT_SPECIES.CANINE),
      active: String(PATIENT_ACTIVATION_STATE.ACTIVE),
      alive: String(PATIENT_DECEASED_STATUS.ALIVE),
      sex: String(PATIENT_GENDER.MALE),
      bloodGroup: String(PATIENT_BLOOD_GROUP.NOT_APPLICABLE),
      mainColour: "#000000",
      secondaryColour: "#ffffff",
      dateOfBirth: todayIso(),
      deceasedDate: "",
      microchipNumber: "Unspecified",
      insuranceRefNumber: "Unspecified",
      passportNumber: "Unspecified",
      desexedStatus: String(PATIENT_DESEXED_STATUS.INTACT),
    },
  });

  const getAgeStringFromDate = (dateValue: string | undefined) => {
    if (!dateValue) return "N/A";
    const date = new Date(`${dateValue}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return "N/A";

    const interval = { start: date, end: Date.now() };
    const duration = intervalToDuration(interval);

    const formatted = formatDuration({
      years: duration.years,
      months: duration.months,
      days: duration.days,
    });

    return formatted || "0 days";
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0) {
      vgToast.error("Select an active clinic before adding a patient.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const species = Number(data.species) || PATIENT_SPECIES.CANINE;
      const gender = Number(data.sex) || PATIENT_GENDER.MALE;
      const blood = Number(data.bloodGroup) || PATIENT_BLOOD_GROUP.NOT_APPLICABLE;
      const isActive = Number(data.active) || PATIENT_ACTIVATION_STATE.ACTIVE;
      const deceasedStatus =
        Number(data.alive) || PATIENT_DECEASED_STATUS.ALIVE;
      const desexed =
        Number(data.desexedStatus) || PATIENT_DESEXED_STATUS.INTACT;
      const dob = toApiDate(data.dateOfBirth);
      const dec = toApiDate(data.deceasedDate);

      const { data: res, error } = await createOrgAnimal(token, {
        orgId,
        name: data.patientsName,
        animalSpecies: species,
        dateOfBirth: dob,
        gender,
        bloodGroup: blood,
        microchipId: data.microchipNumber,
        insuranceReferenceNumber: data.insuranceRefNumber,
        passportNumber: data.passportNumber,
        isActive,
        deceasedStatus,
        deceasedDate: dec,
        desexedStatus: desexed,
        mainColour: normalizeHexColor(data.mainColour),
        secondaryColour: normalizeHexColor(data.secondaryColour, "#ffffff"),
      });
      if (error) {
        vgToast.error(error.message);
        return;
      }
      const newId =
        (res as { newRecordID?: number; NewRecordID?: number })?.newRecordID ??
        (res as { newRecordID?: number; NewRecordID?: number })?.NewRecordID;
      if (newId != null && Number.isFinite(Number(newId))) {
        navigate(`/app/patients/${newId}`);
      } else {
        navigate("/app/patients");
      }
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card
        title="Add New Patient"
        subTitle="Fill the Following Details"
        className="mt-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="p-10">
          <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-10 lg:grid-cols-3">
            <Input
              {...register("patientsName")}
              label="Patients Name"
              placeholder="Enter first name"
            />

            <Select
              {...register("species")}
              label="Species"
              options={toSelectOptions(patientSpeciesOptions)}
            />

            <Select
              {...register("active")}
              label="Activation Status"
              options={toSelectOptions(patientActivationOptions)}
            />

            <Input
              type="date"
              {...register("dateOfBirth")}
              label="Date o Birth"
              placeholder="Enter Date of Birth"
            />

            <div className="flex flex-col gap-y-2 grow">
              <label className="text-gray-400 text-sm">Age</label>
              <p className="border border-gray-300 placeholder:text-sm p-2 min-w-0">
                {getAgeStringFromDate(watch("dateOfBirth"))}
              </p>
            </div>

            <Select
              {...register("alive")}
              label="Deceased Status"
              options={toSelectOptions(patientDeceasedOptions)}
            />

            <Input
              type="date"
              {...register("deceasedDate")}
              label="Deceased Date"
              placeholder="Enter first name"
            />

            <ColorInput
              label="Main Colour"
              value={watch("mainColour")}
              onChange={(value) => setValue("mainColour", value, { shouldDirty: true })}
            />

            <ColorInput
              label="Secondary Colour"
              value={watch("secondaryColour")}
              onChange={(value) =>
                setValue("secondaryColour", value, { shouldDirty: true })
              }
            />

            <Input
              {...register("microchipNumber")}
              label="Microchip Number"
              placeholder="Enter Microchip Number"
            />

            <Input
              {...register("insuranceRefNumber")}
              label="Insurance Ref Number"
              placeholder="Enter Insurance Ref Number"
            />

            <Input
              {...register("passportNumber")}
              label="Passport Number"
              placeholder="Enter Insurance Ref Number"
            />

            <Select
              {...register("sex")}
              label="Sex"
              options={toSelectOptions(patientGenderOptions)}
            />

            <Select
              {...register("bloodGroup")}
              label="Blood Group"
              options={toSelectOptions(patientBloodGroupOptions)}
            />

            <Select
              {...register("desexedStatus")}
              label="De-Sexed Status"
              options={toSelectOptions(patientDesexedOptions)}
            />
          </div>

          <Button color="Primary" type="submit" disabled={submitting}>
            <span>{submitting ? "Creating…" : "Create Patient"}</span>
          </Button>
        </form>
      </Card>
    </Page>
  );
};

export default NewPatient;
